import PropTypes from 'prop-types'
import { createRef, PureComponent } from 'react'
import { Icon } from 'semantic-ui-react'

import { cn } from '@repo/utils'

import { ChildrenSchema } from 'utils'

import Dropzone from '../DropZone'
import FileLink from '../FileLink'
import ProgressBar from '../ProgressBar'
import SimpleButton from '../SimpleButton'

import styles from './index.css'

const SPACE_LIMIT_ALERT = 80
const MAX_UPLOAD_SIZE = 1024 * 1024 * 10 // 10MB

class FileList extends PureComponent {
  state = {
    expanded: false,
    removeMode: false,
  }

  canUploadFile = () =>
    !this.props.readOnly &&
    !this.props.disabled &&
    !this.props.disabledUpload &&
    this.props.spaceLimit > this.props.spaceReached

  dropZoneEl = createRef()

  el = createRef()

  isExpanded = () => this.state.expanded || this.props.expanded

  onDrop = (files, rejections, event) => {
    const { disabledUpload, spaceLimit } = this.props
    let { spaceReached } = this.props
    if (!disabledUpload) {
      const orderedFiles = files
        .sort((a, b) => (a.size > b.size ? 1 : -1))
        .filter((file) => {
          if (spaceLimit < 1 || spaceReached + file.size <= spaceLimit) {
            spaceReached += file.size
            return true
          }

          rejections.push({
            errors: [
              {
                code: 'file-too-large',
                message: 'File is larger than current space',
              },
            ],
            file,
          })
          return false
        })
      this.setState({ removeMode: false })
      this.props.onDrop(orderedFiles, rejections, event)
    }
  }

  onRemove = ({
    currentTarget: {
      dataset: { id },
    },
  }) => this.props.onRemove(this.props.files.find((f) => f.id === id))

  onToggleExpand = ({ key, target }) => {
    if (
      !this.props.disabled &&
      target === this.el.current &&
      (!key || // Click
        key === 'Enter' ||
        key === ' ')
    ) {
      this.setState((prevState) => ({
        expanded: this.props.expanded || !prevState.expanded,
        removeMode: false,
      }))
    }
  }

  onToggleRemoveMode = () => this.setState((prevState) => ({ removeMode: !prevState.removeMode }))

  shouldRenderFilesList = () => this.props.files.length > 0

  shouldRenderProgressBar = () => this.isExpanded() && this.props.spaceLimit > 0

  renderFilesList() {
    return (
      <ol className={cn(styles.list, this.isExpanded() && styles.expanded)}>
        {this.props.files.map((f) => (
          <li key={f.id} className={styles.file}>
            {this.state.removeMode && !this.props.disabled && !this.props.readOnly && !f.loading && (
              <SimpleButton
                ariaLabel={`Eliminar ${f.name}`}
                className={styles.removeIcon}
                data-id={f.id}
                icon="trash alternate outline"
                onClick={this.onRemove}
              />
            )}
            <FileLink {...f} />
          </li>
        ))}
      </ol>
    )
  }

  renderProgressBar() {
    const percentage = (this.props.spaceReached / this.props.spaceLimit) * 100
    return (
      <ProgressBar
        alert={percentage > SPACE_LIMIT_ALERT}
        ariaLabel={`Utilizado el ${+percentage.toFixed(2)}% del espacio adquirido`}
        className={styles.progressBar}
        value={percentage}
      />
    )
  }

  render() {
    const { ariaLabel, className, comment, disabled, expanded, readOnly, tabIndex } = this.props

    return (
      <Dropzone
        accept={{}}
        allowPaste={!disabled && !readOnly}
        className={cn(styles.dropzone, className, !this.shouldRenderFilesList() && 'emptyField emptySection')}
        disabled={disabled || readOnly}
        innerRef={this.dropZoneEl}
        maxSize={MAX_UPLOAD_SIZE}
        multiple
        onDrop={this.onDrop}
      >
        <div
          aria-label={ariaLabel}
          className={cn(styles.wrapper, disabled && styles.disabled, !this.props.expanded && styles.expandable)}
          onClick={this.onToggleExpand}
          onKeyDown={this.onToggleExpand}
          ref={this.el}
          role="menu"
          tabIndex={disabled ? -1 : tabIndex}
        >
          <div className={styles.header}>
            <div className={styles.icon}>
              <Icon name="attach" />
            </div>
            <div>{comment}</div>
            {!expanded && !disabled && this.shouldRenderFilesList() && (
              <Icon name={this.isExpanded() ? 'angle up' : 'angle down'} />
            )}
          </div>
          {this.shouldRenderFilesList() && this.renderFilesList()}
          {this.shouldRenderProgressBar() && this.renderProgressBar()}
          {this.canUploadFile() && this.isExpanded() && (
            <div className={styles.actions}>
              <SimpleButton icon="upload" onClick={() => this.dropZoneEl.current.open()} primary value="Agregar" />
              {this.shouldRenderFilesList() && (
                <label className={styles.removeToggle}>
                  <input checked={this.state.removeMode} onChange={this.onToggleRemoveMode} type="checkbox" />
                  Eliminación
                </label>
              )}
            </div>
          )}
        </div>
      </Dropzone>
    )
  }
}

FileList.propTypes = {
  files: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      disabled: PropTypes.bool,
      href: PropTypes.string,
      loading: PropTypes.bool,
      name: PropTypes.string.isRequired,
      size: PropTypes.number,
    }),
  ).isRequired,
  onDrop: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,

  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  comment: ChildrenSchema,
  disabled: PropTypes.bool,
  disabledUpload: PropTypes.bool,
  expanded: PropTypes.bool,
  readOnly: PropTypes.bool,
  spaceLimit: PropTypes.number,
  spaceReached: PropTypes.number,
  tabIndex: PropTypes.number,
}

FileList.defaultProps = {
  ariaLabel: '',
  className: '',
  comment: null,
  disabled: false,
  disabledUpload: false,
  expanded: false,
  readOnly: false,
  spaceLimit: 0,
  spaceReached: 0,
  tabIndex: 0,
}

export default FileList
